import { IFacebookLoginPayload } from 'models/auth';
import api from './api';
import { AUTH_METHODS, FACEBOOK_AUTH_TYPES, FACEBOOK_MANUAL_AUTH_REDIRECT, FACEBOOK_SDK_VERSION } from './constants';
import { getQueryParamByName, normalizeForUrl } from './helpers';
import history from 'util/history';
import { IGridFunctions } from 'models/grid';

let initialPromise: any = null;


export const FB_ERROR_TYPE = {
  GENERIC: 'generic',
  PERMISSIONS: 'permissions'
}

export const checkLoginState = (): Promise<any> => {
  if (!initialPromise) {
    throw new Error('fb_helpers.js initializeFbSdk must be called before any other helper functions.');
  }

  return new Promise((resolve: (value: any) => void, reject: any) => {
    initialPromise.then(() => {
      return window.FB && window.FB.getLoginStatus((response: any) => {
        if (response.status === 'connected') {
          return getMyInfo()
            .then(response => resolve(response));
        }
        else { return reject( response ); }
      });
    });
  });
};

export const checkLogoutState = (): Promise<any> => {
  if (!initialPromise) {
    throw new Error('fb-helpers.js initializeFbSdk must be called before any other helper functions.');
  }


  return new Promise<any>((resolve: (value: any) => void, reject: any): void => {
    initialPromise.then(() => {
      window.FB && window.FB.getLoginStatus((response: any) => {
        if (response.status !== 'connected') {
          return resolve(response);
        }
        else { return reject('Unable to connect to Facebook'); }
      });
    });
  });
};

const getMyInfo = (): Promise<any> => {
  return new Promise((resolve: (value: any) => void) => {
    window.FB.api('/me', { fields: 'email,first_name,last_name' }, (response: any) => {
      resolve(response);
    });
  });
};

export const initializeFbSdk = (id: string) => {
  // return early if no id
  if (!id) {
    return;
  }

  if (initialPromise) {
    return;
  }
  initialPromise = new Promise((resolve) => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: id,
        autoLogAppEvents: true,
        xfbml: true,
        version: FACEBOOK_SDK_VERSION
      });

      resolve(window.FB);
    };
  });

  (function (d, s, id) {
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) { return; }
    js = d.createElement(s); js.id = id;
    (js as HTMLScriptElement).src = "https://connect.facebook.net/en_US/sdk.js";
    if (fjs && fjs.parentNode) {
      fjs.parentNode.insertBefore(js, fjs);
    }
  }(document, 'script', 'facebook-jssdk'));

  return initialPromise;
};

/**
 * Serialize a plain object, simliar to jQuery.param()
 * @returns string of serialized data
 */
const serializePlainObject = (obj: any) => {
  let serialized = '';
  for (let key in obj) serialized += '&' + key + '=' + encodeURIComponent(obj[key]);
  return serialized.substring(1);
};
/**
 * Facebook share using a PHP proxy
 * @param data - plain object
 * @param url - the base url for FB share
 */
export const share = (url: string, data?: any) => {
  const shareURL = data ? `${url}?${serializePlainObject(data)}` : url;

  window.FB.ui({
    method: 'share',
    display: 'popup',
    href: shareURL
  });
};

export const manualOAuth = (accessToken: string, accessTokenExpiration: number): Promise<any> => {
  if (!initialPromise) {
    throw new Error('fb_helpers.js initializeFbSdk must be called before any other helper functions.');
  }

  return new Promise((resolve, reject) => {
    initialPromise.then(() => {
      return window.FB.api('/me', {
        fields: 'id,email,first_name,last_name,permissions',
        access_token: accessToken
      }, (response: any) => {
        resolve(response);
      });
    });
  });
};

export const buildManualLoginURL = (appId: string, redirectUri: string, redirectState: string = '', ) => {
  const clientID = `client_id=${appId}`;
  const responseType = 'response_type=token%20granted_scopes';
  const scope = 'scope=email';
  const state = `state=${redirectState}`;
  const authType = `auth_type=${FACEBOOK_AUTH_TYPES.REREQUEST}`;
  const redirectURI = `redirect_uri=${encodeURIComponent( redirectUri )}`;
  const params = `${clientID}&${responseType}&${scope}&${redirectURI}&${state}&${authType}`;
  const baseURL = encodeURI(`https://www.facebook.com/${FACEBOOK_SDK_VERSION}/dialog/oauth?`);

  return `${baseURL}${params}`;
}

interface FacebookRedirectState {
  path: {
    categoryId: string,
    contestantId: string
  },
  lang: string,
  form: {
    [key: string]: boolean
  }
}

export const parseFacebookStateParams = (): FacebookRedirectState => {
  const parentUrl = getQueryParamByName('parentUrl');
  const stateParam = getQueryParamByName('state', parentUrl);
  try {
    const redirectState = JSON.parse( stateParam );
    return redirectState;
  } catch( e ) {
    return {
      path: {
        categoryId: '',
        contestantId: ''
      },
      lang: api.defaults.lang,
      form: {
        
      }
    };
  }
}

export const getFacebookTokens = () => {
  const url = getQueryParamByName('parentUrl');
  const accessToken = getQueryParamByName('#access_token', url) || getQueryParamByName('access_token', url);
  const accessTokenExpiration = parseInt(getQueryParamByName('expires_in', url) || '0');

  return {
    accessToken,
    accessTokenExpiration
  }
}

export const getFacebookScopes = () => {
  const url = getQueryParamByName('parentUrl');
  const granted = getQueryParamByName('granted_scopes', url);
  const denied = getQueryParamByName('denied_scopes', url);

  return {
    granted,
    denied
  };
}

export const getFacebookErrorParams = () => {
  const url = getQueryParamByName('parentUrl');
  const errorCode = getQueryParamByName('error_code', url);
  const error = getQueryParamByName('error', url);
  const errorDescription = getQueryParamByName('error_description', url);
  const errorReason = getQueryParamByName('error_reason', url);

  return {
    error,
    errorCode,
    errorDescription,
    errorReason
  };
}

export const getManualLoginUserData = async ( accessToken: string, accessTokenExpiration: number ) => {
  const prevState = parseFacebookStateParams();

  if( prevState.form?.optin_terms ) {
    const response = await manualOAuth( accessToken, accessTokenExpiration );

    const resPayload = {
      method: AUTH_METHODS.FACEBOOK,
      fb_email: response.email,
      fb_first_name: response.first_name,
      fb_last_name: response.last_name,
      user_id: response.id,
      ...prevState.form
    } as IFacebookLoginPayload;

    const payload = { ...resPayload } as IFacebookLoginPayload;
    return payload;
  }

  return null;
}

export const restorePreviousRoute = async ( gridFn: IGridFunctions, loadContestantModal: boolean = true ) => {
  const prevState = parseFacebookStateParams();

  const { categoryId, contestantId } = prevState.path;
  const isCategoryVote = true;

  if( categoryId && contestantId && loadContestantModal ) {
    const contestantName = normalizeForUrl( gridFn.getContestantById( contestantId, categoryId ).name );
    const categoryName = normalizeForUrl( gridFn.getCategoryById( categoryId ).name );
    const pathname = isCategoryVote ? `/${categoryName}/${contestantName}` : `/${contestantName}`;
    history.push( {
      pathname,
      search: history.location.search
    } );
  } else if( categoryId && isCategoryVote) {
    const categoryName = normalizeForUrl( gridFn.getCategoryById( categoryId ).name );
    const pathname = `/${categoryName}/`;

    history.push( {
      pathname,
      search: history.location.search
    } );
  }
}